<!-- @format -->

<template>
	<disabled-text v-if="disabled && showDisabledText" :value="value" :params="params" />
	<template v-else>
		<el-tree-select
			v-if="isDict"
			v-model="value"
			:disabled="disabled"
			:data="params"
			clearable
			filterable
			default-first-option
			placeholder="请选择下拉数据"
			class="select"
			v-bind="$attrs"
		/>
		<el-select
			v-else
			v-model="value"
			:disabled="disabled"
			clearable
			filterable
			default-first-option
			placeholder="请选择下拉数据"
			class="select"
			check-strictly
			v-bind="$attrs"
		>
			<el-option v-for="item in params" :key="item.label" :label="item.label" :value="item.value" />
		</el-select>
	</template>
</template>

<script setup lang="ts">
	import DisabledText from './disabledText.vue';
	import { useDictList, findTreeDataWithNoRoot } from '@/api';
	import { getCache, setCache } from '@/utils/util';

	interface Props {
		modelValue: string;
		params?: { label: string; value: string; [key: string]: any }[];
		disabled?: boolean;
		showDisabledText?: boolean;
		citeType?: string;
		isDict?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		modelValue: '',
		showDisabledText: true,
		isDict: false,
	});

	const emit = defineEmits(['update:modelValue']);

	const attr = useAttrs();

	const value = computed({
		get() {
			if (attr.multiple) {
				return props.modelValue ? props.modelValue.split(',') : [];
			} else {
				return props.modelValue;
			}
		},
		set(val) {
			emit('update:modelValue', val ? val?.toString() : '');
		},
	});

	const data = ref<{ label: string; value: string }[]>([]);

	const params = computed(() => (props.isDict || props.citeType ? data.value : props.params) || []);

	const getDictList = async () => {
		if (props.isDict) {
			const cacheAll = getCache('dictCache', 'dictCacheAll');
			if (cacheAll) {
				data.value = arrayToTree(cacheAll || []);
			} else {
				const res = await findTreeDataWithNoRoot();
				setCache('dictCache', 'dictCacheAll', res);
				data.value = arrayToTree(res || []);
			}
		} else if (props.citeType) {
			const cacheRes = getCache('dictCache', props.citeType);
			if (cacheRes) {
				data.value = cacheRes;
			} else {
				const res = await useDictList({ dictType: props.citeType });
				setCache('dictCache', props.citeType, res);
				data.value = res;
			}
		}
	};

	function arrayToTree(data, pIdKey = 'parentId', idKey = 'id', childrenKey = 'children') {
		const sNodes = JSON.parse(JSON.stringify(data));
		let i, j, l;
		if (!idKey || idKey === '' || !sNodes) {
			return [];
		}
		if (sNodes instanceof Array) {
			const r: any = [];
			const tmpMap: any = [];
			for (i = 0, l = sNodes.length; i < l; i++) {
				tmpMap[sNodes[i][idKey]] = sNodes[i];
			}

			for (j = 0, l = sNodes.length; j < l; j++) {
				// 判断是否是叶子节点
				if (tmpMap[sNodes[j][pIdKey]] && sNodes[j][idKey] !== sNodes[j][pIdKey]) {
					if (!tmpMap[sNodes[j][pIdKey]][childrenKey]) {
						tmpMap[sNodes[j][pIdKey]][childrenKey] = [];
					}
					tmpMap[sNodes[j][pIdKey]][childrenKey].push(sNodes[j]);
				} else {
					r.push(sNodes[j]);
				}
			}
			return r;
		} else {
			return [sNodes];
		}
	}

	getDictList();
</script>

<style scoped>
	.select {
		width: 100%;
	}

	.show-value {
		display: flex;
		gap: 3px;
		flex-wrap: wrap;
	}
</style>
